import { BroomIcon } from "@phosphor-icons/react";
import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

export interface AnimatedBroomIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface AnimatedBroomIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  autoAnimate?: boolean;
  isHovered?: boolean;
}

const SWEEPING_VARIANTS: Variants = {
  normal: {
    rotate: 0,
    x: 0,
  },
  animate: {
    rotate: [-15, 15, -15],
    x: [-2, 2, -2],
    transition: {
      repeat: Number.POSITIVE_INFINITY,
      repeatType: "mirror" as const,
      duration: 0.8,
      ease: "easeInOut",
    },
  },
};

const AnimatedBroomIcon = forwardRef<
  AnimatedBroomIconHandle,
  AnimatedBroomIconProps
>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 16,
      autoAnimate = false,
      isHovered,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);
    const [internalHovered, setInternalHovered] = useState(false);

    // Use external isHovered prop if provided, otherwise use internal state
    const shouldAnimate = isHovered !== undefined ? isHovered : internalHovered;

    useEffect(() => {
      if (isControlledRef.current) return;

      if (autoAnimate) {
        controls.start("animate");
      } else {
        controls.start(shouldAnimate ? "animate" : "normal");
      }
    }, [autoAnimate, controls, shouldAnimate]);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e);
        // Only use internal hover state if isHovered prop is not provided
        if (!isControlledRef.current && isHovered === undefined) {
          setInternalHovered(true);
        }
      },
      [onMouseEnter, isHovered],
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e);
        // Only use internal hover state if isHovered prop is not provided
        if (!isControlledRef.current && isHovered === undefined) {
          setInternalHovered(false);
        }
      },
      [onMouseLeave, isHovered],
    );

    return (
      <div
        className={cn("inline-flex items-center justify-center", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <motion.div
          animate={controls}
          variants={SWEEPING_VARIANTS}
          style={{ originX: "50%", originY: "50%", display: "inline-flex" }}
        >
          <BroomIcon size={size} weight="regular" />
        </motion.div>
      </div>
    );
  },
);

AnimatedBroomIcon.displayName = "AnimatedBroomIcon";

export { AnimatedBroomIcon };
